import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search, Users, X, BookOpen, ArrowRight, UserPlus, Plus, Minus
} from 'lucide-react';
import { topicsAPI, sponsorshipsAPI, departmentsAPI } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Badge, Spinner, EmptyState, formatCurrency, PageHeader, SkillTags, Modal, Alert } from '../components/shared';
import toast from 'react-hot-toast';
import api from '../lib/api';

export default function MarketplacePage() {
  const { user, isApproved } = useAuth();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [filters, setFilters] = useState({ domain: '', status: '', is_sponsored: '', search: '', page: 1 });
  const [domains, setDomains] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [applyModal, setApplyModal] = useState(null);
  const [sponsorModal, setSponsorModal] = useState(null);

  useEffect(() => { topicsAPI.getDomains().then(r => setDomains(r.data)); }, []);

  const fetchTopics = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: filters.page, limit: 12 };
      if (filters.domain) params.domain = filters.domain;
      if (filters.status) params.status = filters.status;
      if (filters.is_sponsored) params.is_sponsored = filters.is_sponsored;
      if (filters.search) params.search = filters.search;
      const res = await topicsAPI.getAll(params);
      setTopics(res.data.topics);
      setPagination(res.data.pagination);
    } catch { toast.error('Failed to load topics'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { fetchTopics(); }, [fetchTopics]);

  const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value, page: 1 }));

  return (
    <div>
      <PageHeader
        eyebrow="Research Marketplace"
        title="Available Topics"
        subtitle="Browse research domains submitted by lecturers and find your project"
        actions={
          user?.role === 'student' && isApproved && (
            <Link to="/dashboard/applications?tab=independent" className="btn btn-secondary">
              <BookOpen size={15} /> Submit Independent Project
            </Link>
          )
        }
      />

      {/* Search & Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 28, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 240 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input className="form-input" style={{ paddingLeft: 40 }} placeholder="Search topics, keywords…"
            value={filters.search} onChange={e => handleFilterChange('search', e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 'auto' }} value={filters.status} onChange={e => handleFilterChange('status', e.target.value)}>
          <option value="">All Statuses</option>
          <option value="open">Open</option>
          <option value="sponsored">Sponsored</option>
          <option value="assigned">Assigned</option>
        </select>
        <select className="form-select" style={{ width: 'auto' }} value={filters.is_sponsored} onChange={e => handleFilterChange('is_sponsored', e.target.value)}>
          <option value="">All Funding</option>
          <option value="true">Sponsored Only</option>
          <option value="false">Unsponsored</option>
        </select>
        {(filters.search || filters.status || filters.is_sponsored) && (
          <button className="btn btn-ghost btn-sm" onClick={() => setFilters({ domain: filters.domain, status: '', is_sponsored: '', search: '', page: 1 })}>
            <X size={14} /> Clear
          </button>
        )}
      </div>

      {/* Domain pills */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 }}>
        {['', ...domains.map(d => d.domain)].map((d, i) => (
          <button key={i} onClick={() => handleFilterChange('domain', d)} style={{
            padding: '5px 14px', borderRadius: 'var(--radius-full)',
            border: `1px solid ${filters.domain === d ? 'var(--gold)' : 'rgba(255,255,255,0.1)'}`,
            background: filters.domain === d ? 'rgba(201,162,39,0.12)' : 'transparent',
            color: filters.domain === d ? 'var(--gold)' : 'var(--text-secondary)',
            fontSize: '0.8rem', cursor: 'pointer', transition: 'all 0.2s', fontFamily: 'var(--font-mono)',
          }}>
            {d || 'All Domains'}
          </button>
        ))}
      </div>

      {!loading && (
        <div style={{ marginBottom: 20, color: 'var(--text-muted)', fontSize: '0.8rem', fontFamily: 'var(--font-mono)' }}>
          {pagination.total || 0} topics found
        </div>
      )}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}><Spinner size={32} /></div>
      ) : topics.length === 0 ? (
        <EmptyState icon={BookOpen} title="No topics found" description="Try adjusting your filters or check back later." />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {topics.map(topic => (
            <TopicCard key={topic.id} topic={topic} userRole={user?.role} isApproved={isApproved}
              onApply={() => setApplyModal(topic)} onSponsor={() => setSponsorModal(topic)}
              onClick={() => setSelectedTopic(topic)} />
          ))}
        </div>
      )}

      {pagination.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 40 }}>
          {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
            <button key={page} onClick={() => setFilters(f => ({ ...f, page }))} style={{
              width: 36, height: 36, borderRadius: 'var(--radius-md)',
              border: `1px solid ${filters.page === page ? 'var(--gold)' : 'rgba(255,255,255,0.1)'}`,
              background: filters.page === page ? 'rgba(201,162,39,0.12)' : 'transparent',
              color: filters.page === page ? 'var(--gold)' : 'var(--text-secondary)',
              cursor: 'pointer', fontSize: '0.875rem'
            }}>{page}</button>
          ))}
        </div>
      )}

      <TopicDetailModal topic={selectedTopic} onClose={() => setSelectedTopic(null)}
        userRole={user?.role} isApproved={isApproved}
        onApply={t => { setSelectedTopic(null); setApplyModal(t); }}
        onSponsor={t => { setSelectedTopic(null); setSponsorModal(t); }} />

      <ApplyModal topic={applyModal} onClose={() => setApplyModal(null)}
        onSuccess={() => { setApplyModal(null); fetchTopics(); }} />

      <SponsorModal topic={sponsorModal} onClose={() => setSponsorModal(null)}
        onSuccess={() => { setSponsorModal(null); fetchTopics(); }} />
    </div>
  );
}

// ── Topic Card ────────────────────────────────────────────────────────────────
function TeamBadge({ topic }) {
  if (!topic.team_enabled) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem',
      background: 'rgba(201,168,76,0.12)', color: 'var(--gold)',
      border: '1px solid rgba(201,168,76,0.25)', fontFamily: 'var(--font-mono)',
    }}>
      <Users size={10} />
      Team project{topic.max_team_size ? ` · max ${topic.max_team_size}` : ''}
    </span>
  );
}

function TopicCard({ topic, userRole, isApproved, onApply, onSponsor, onClick }) {
  return (
    <div className={`topic-card ${topic.is_sponsored ? 'sponsored' : ''}`} onClick={onClick}>
      <div className="topic-card-header">
        <div style={{ flex: 1 }}>
          {topic.domain && <div className="eyebrow" style={{ marginBottom: 6 }}>{topic.domain}</div>}
          <h3 className="topic-card-title">{topic.title}</h3>
          <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
            <Badge status={topic.status} />
            <TeamBadge topic={topic} />
          </div>
        </div>
      </div>
      <p className="topic-card-desc">{topic.description}</p>
      {topic.required_skills?.length > 0 && <SkillTags skills={topic.required_skills} max={3} />}
      <div className="topic-card-footer">
        <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
          {topic.application_mode !== 'student_own' && topic.poster_role !== 'student' && <span className="topic-meta"><Users size={12} />{topic.application_count || 0} applicant{topic.application_count !== 1 ? 's' : ''}</span>}
          {parseFloat(topic.total_funding) > 0 && (
            <span className="topic-meta" style={{ color: 'var(--gold)' }}>{formatCurrency(topic.total_funding)}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {userRole === 'student' && isApproved && ['open', 'sponsored'].includes(topic.status) && (
            <button className="btn btn-primary btn-sm" onClick={e => { e.stopPropagation(); onApply(topic); }}>
              {topic.team_enabled && topic.application_mode === 'pre_formed' ? <><UserPlus size={12} /> Apply as Team</> : 'Apply'}
            </button>
          )}
          {(userRole === 'alumni' || userRole === 'judge') && isApproved && ['open', 'sponsored'].includes(topic.status) && (
            <button className="btn btn-teal btn-sm" onClick={e => { e.stopPropagation(); onSponsor(topic); }}>
              ₦ Sponsor
            </button>
          )}
        </div>
      </div>
      <div style={{ paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.05)', fontSize: '0.78rem', color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
        <span>{topic.lecturer_title ? `${topic.lecturer_title} ` : ''}{topic.lecturer_name}</span>
        <span>{topic.department}</span>
      </div>
    </div>
  );
}

// ── Topic Detail Modal ────────────────────────────────────────────────────────
function TopicDetailModal({ topic, onClose, userRole, isApproved, onApply, onSponsor }) {
  if (!topic) return null;
  return (
    <Modal open={!!topic} onClose={onClose} title="" size="lg">
      <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        {topic.domain && <div className="eyebrow">{topic.domain}</div>}
        <Badge status={topic.status} />
        {topic.is_sponsored && <Badge status="sponsored">Sponsored</Badge>}
        <TeamBadge topic={topic} />
      </div>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.75rem', fontWeight: 400, lineHeight: 1.2, marginBottom: 20 }}>{topic.title}</h2>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>
            {topic.poster_role === 'alumni' ? 'Posted by (Alumni)' : topic.poster_role === 'student' ? 'Posted by (Student)' : 'Lecturer'}
          </div>
          <div style={{ fontWeight: 500 }}>{topic.lecturer_title} {topic.lecturer_name}</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{topic.department}</div>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Applicants</div>
          {topic.application_mode !== 'student_own' && topic.poster_role !== 'student' && <div>{topic.application_count || 0} applicant{topic.application_count !== 1 ? 's' : ''}</div>}
        </div>
        {parseFloat(topic.total_funding) > 0 && (
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Funding Pledged</div>
            <div style={{ color: 'var(--gold)', fontFamily: 'var(--font-mono)', fontSize: '1.1rem' }}>{formatCurrency(topic.total_funding)}</div>
          </div>
        )}
        {topic.application_deadline && (
          <div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 4 }}>Application Deadline</div>
            <div>{new Date(topic.application_deadline).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' })}</div>
          </div>
        )}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div className="form-label" style={{ marginBottom: 8 }}>Description</div>
        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>{topic.description}</p>
      </div>

      {topic.expected_outcomes && (
        <div style={{ marginBottom: 20 }}>
          <div className="form-label" style={{ marginBottom: 8 }}>Expected Outcomes</div>
          <p style={{ color: 'var(--text-secondary)', lineHeight: 1.7 }}>{topic.expected_outcomes}</p>
        </div>
      )}

      {topic.required_skills?.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div className="form-label" style={{ marginBottom: 8 }}>Required Skills</div>
          <SkillTags skills={topic.required_skills} />
        </div>
      )}

      {/* Team info block */}
      {topic.team_enabled && (
        <div style={{ marginBottom: 20, padding: '14px 16px', background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.2)', borderRadius: 10 }}>
          <div style={{ fontWeight: 600, color: 'var(--gold)', marginBottom: 8, fontSize: '0.875rem' }}>
            <Users size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
            This is a team project
          </div>
          {topic.application_mode === 'pre_formed' && (
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Form your team on the platform before applying. All members must accept their invitation before the application can be submitted.
            </p>
          )}
          {topic.application_mode === 'individual' && (
            <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5 }}>
              Apply individually. The sponsor will select team members from approved applicants.
            </p>
          )}
          {topic.max_team_size && (
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 6, marginBottom: 0 }}>
              Maximum team size: {topic.max_team_size}
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.07)' }}>
        {userRole === 'student' && isApproved && ['open', 'sponsored'].includes(topic.status) && (
          <button className="btn btn-primary" onClick={() => onApply(topic)}>
            {topic.team_enabled && topic.application_mode === 'pre_formed'
              ? <><UserPlus size={15} /> Apply as a Team</>
              : <>Apply for this Topic <ArrowRight size={15} /></>}
          </button>
        )}
        {(userRole === 'alumni' || userRole === 'judge') && isApproved && ['open', 'sponsored'].includes(topic.status) && (
          <button className="btn btn-teal" onClick={() => onSponsor(topic)}>
            ₦ Sponsor this Topic
          </button>
        )}
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
      </div>
    </Modal>
  );
}

// ── Apply Modal — handles solo and pre-formed team ────────────────────────────
function ApplyModal({ topic, onClose, onSuccess }) {
  // Step: 'form' (solo) | 'team_setup' | 'team_application'
  const [step, setStep] = useState('form');
  const [form, setForm] = useState({
    participation_type: 'lecturer_sponsored',
    statement_of_interest: '',
    relevant_experience: '',
    proposed_approach: '',
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Team state
  const [teamName, setTeamName] = useState('');
  const [teammateEmail, setTeammateEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [searching, setSearching] = useState(false);
  const [teammates, setTeammates] = useState([]); // { id, first_name, last_name, department }
  const [teamId, setTeamId] = useState(null);
  const [teamStatus, setTeamStatus] = useState(null); // null | 'pending' | 'ready'
  const [teamMembers, setTeamMembers] = useState([]);
  const [pollInterval, setPollInterval] = useState(null);

  const isPreFormed = topic?.team_enabled && topic?.application_mode === 'pre_formed';

  // Reset on open + check for existing team
  useEffect(() => {
    if (topic) {
      setStep('form');
      setForm({ participation_type: 'lecturer_sponsored', statement_of_interest: '', relevant_experience: '', proposed_approach: '' });
      setFile(null); setTeamName(''); setTeammates([]); setTeamId(null); setTeamStatus(null);
      setTeammateEmail(''); setEmailError('');

      // If pre-formed topic, check if student already has a team for this topic
      if (topic.team_enabled && topic.application_mode === 'pre_formed') {
        api.get(`/teams/my-team-for-topic?topic_id=${topic.id}`)
          .then(res => {
            if (res.data) {
              const team = res.data;
              setTeamId(team.team_id);
              setTeamMembers(team.members || []);
              const hasPending = parseInt(team.pending_count) > 0;
              setTeamStatus(hasPending ? 'pending' : 'ready');
              setStep('team_application');
            }
          })
          .catch(() => {});
      }
    }
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, [topic]);

  const handleAddByEmail = async () => {
    if (!teammateEmail.trim()) return;
    setEmailError('');
    setSearching(true);
    try {
      const res = await api.get(`/profiles/search-by-email?email=${encodeURIComponent(teammateEmail.trim())}`);
      const student = res.data;
      if (teammates.find(t => t.id === student.id)) { setEmailError('This student is already in your team.'); return; }
      if (topic.max_team_size && teammates.length + 1 >= topic.max_team_size) {
        setEmailError(`Maximum team size is ${topic.max_team_size} (including you)`);
        return;
      }
      setTeammates(t => [...t, student]);
      setTeammateEmail('');
    } catch (err) {
      setEmailError(err.response?.data?.error || 'No approved student found with that email.');
    } finally {
      setSearching(false);
    }
  };

  const removeTeammate = (id) => setTeammates(t => t.filter(m => m.id !== id));

  const handleCreateTeam = async () => {
    if (!teamName.trim()) { toast.error('Enter a team name'); return; }
    if (teammates.length === 0) { toast.error('Add at least one teammate'); return; }
    setLoading(true);
    try {
      const res = await api.post('/teams', {
        topic_id: topic.id,
        team_name: teamName.trim(),
        teammate_ids: teammates.map(t => t.id),
      });
      setTeamId(res.data.id);
      setTeamMembers(res.data.members || []);
      const hasPending = (res.data.members || []).some(m => m.status === 'invited');
      setTeamStatus(hasPending ? 'pending' : 'ready');
      toast.success('Team created! Invitations sent.');
      setStep('team_application');

      // Poll for invitation responses every 30s
      const interval = setInterval(async () => {
        try {
          const r = await api.get(`/teams/${res.data.id}`);
          setTeamMembers(r.data.members || []);
          const pending = (r.data.members || []).some(m => m.status === 'invited');
          setTeamStatus(pending ? 'pending' : 'ready');
          if (!pending) clearInterval(interval);
        } catch { clearInterval(interval); }
      }, 30000);
      setPollInterval(interval);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create team');
    } finally {
      setLoading(false);
    }
  };

  const handleSoloSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { applicationsAPI } = await import('../lib/api');
      await applicationsAPI.apply({ ...form, topic_id: topic.id }, file);
      toast.success('Application submitted successfully!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  const handleTeamSubmit = async () => {
    if (!form.statement_of_interest.trim()) { toast.error('Statement of interest is required'); return; }
    setLoading(true);
    try {
      const { applicationsAPI } = await import('../lib/api');
      await applicationsAPI.apply({
        topic_id: topic.id,
        team_id: teamId,
        statement_of_interest: form.statement_of_interest,
        relevant_experience: form.relevant_experience,
        proposed_approach: form.proposed_approach,
      }, file);
      toast.success('Team application submitted!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (!topic) return null;

  // ── Pre-formed: Step 1 — Team Setup ──────────────────────────────────────
  if (isPreFormed && step === 'form') {
    return (
      <Modal open={!!topic} onClose={onClose} title={`Apply as Team: ${topic.title}`} size="lg">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Alert type="info">
            Form your team before applying. All teammates must accept their invitations before you can submit the application.
          </Alert>

          <div className="form-group">
            <label className="form-label">Team Name *</label>
            <input className="form-input" placeholder="e.g. Team Voltage" value={teamName} onChange={e => setTeamName(e.target.value)} />
          </div>

          <div className="form-group">
            <label className="form-label">
              Invite Teammates by Email
              {topic.max_team_size && <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> (max {topic.max_team_size - 1} teammates)</span>}
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" type="email" placeholder="teammate@example.com"
                value={teammateEmail} onChange={e => { setTeammateEmail(e.target.value); setEmailError(''); }}
                onKeyDown={e => e.key === 'Enter' && handleAddByEmail()} />
              <button className="btn btn-ghost btn-sm" onClick={handleAddByEmail} disabled={searching || !teammateEmail.trim()}>
                {searching ? <Spinner size={14} /> : <><Plus size={14} /> Add</>}
              </button>
            </div>
            {emailError && (
              <div style={{ fontSize: '0.78rem', color: 'var(--error)', marginTop: 6 }}>{emailError}</div>
            )}

            {teammates.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>
                  Your team ({teammates.length + 1} / {topic.max_team_size || '∞'} members including you):
                </div>
                {teammates.map(t => (
                  <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: '0.875rem' }}>{t.first_name} {t.last_name} <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>· {t.department}</span></span>
                    <button className="btn btn-ghost btn-sm" onClick={() => removeTeammate(t.id)} style={{ color: 'var(--error)', padding: '2px 6px' }}>
                      <Minus size={13} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleCreateTeam} disabled={loading || !teamName.trim() || teammates.length === 0}>
              {loading ? <Spinner size={16} /> : <><UserPlus size={15} /> Create Team & Send Invitations</>}
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // ── Pre-formed: Step 2 — Await Acceptance + Application Form ─────────────
  if (isPreFormed && step === 'team_application') {
    const allAccepted = teamStatus === 'ready';
    return (
      <Modal open={!!topic} onClose={onClose} title={`Team Application: ${topic.title}`} size="lg">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Member status panel */}
          <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden' }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.07)', fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Team Members — Invitation Status
            </div>
            {teamMembers.map(m => (
              <div key={m.student_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <span style={{ fontSize: '0.875rem' }}>
                  {m.first_name} {m.last_name}
                  {m.is_lead && <span style={{ marginLeft: 8, fontSize: '0.72rem', color: 'var(--gold)', background: 'rgba(201,168,76,0.1)', padding: '1px 6px', borderRadius: 4 }}>Lead</span>}
                </span>
                <span style={{ fontSize: '0.75rem', fontFamily: 'var(--font-mono)', color: m.status === 'accepted' ? 'var(--success)' : m.status === 'declined' ? 'var(--error)' : 'var(--warning)' }}>
                  {m.status}
                </span>
              </div>
            ))}
          </div>

          {!allAccepted && (
            <Alert type="info">
              Waiting for all teammates to accept. The application form will unlock once everyone has responded.
              This panel refreshes automatically every 30 seconds.
            </Alert>
          )}

          {allAccepted && (
            <>
              <div className="form-group">
                <label className="form-label">Statement of Interest *</label>
                <textarea className="form-textarea" style={{ minHeight: 120 }}
                  placeholder="Explain why your team is interested in this topic and what makes you a good fit…"
                  value={form.statement_of_interest}
                  onChange={e => setForm(f => ({ ...f, statement_of_interest: e.target.value }))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Relevant Experience</label>
                <textarea className="form-textarea" style={{ minHeight: 80 }}
                  placeholder="Describe your team's relevant coursework, projects, or skills…"
                  value={form.relevant_experience}
                  onChange={e => setForm(f => ({ ...f, relevant_experience: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Proposed Approach</label>
                <textarea className="form-textarea" style={{ minHeight: 80 }}
                  placeholder="Briefly outline how your team would approach this topic…"
                  value={form.proposed_approach}
                  onChange={e => setForm(f => ({ ...f, proposed_approach: e.target.value }))} />
              </div>
              <div className="form-group">
                <label className="form-label">Supporting Document (PDF)</label>
                <input type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files[0])} style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }} />
                {file && <span style={{ fontSize: '0.8rem', color: 'var(--teal)' }}>✓ {file.name}</span>}
              </div>
            </>
          )}

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            {allAccepted && (
              <button className="btn btn-primary" onClick={handleTeamSubmit} disabled={loading}>
                {loading ? <Spinner size={16} /> : 'Submit Team Application'}
              </button>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  // ── Solo / individual application ─────────────────────────────────────────
  return (
    <Modal open={!!topic} onClose={onClose} title={`Apply: ${topic?.title || ''}`} size="lg">
      <form onSubmit={handleSoloSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div className="form-group">
          <label className="form-label">Participation Type *</label>
          <select className="form-select" value={form.participation_type} onChange={e => setForm(f => ({ ...f, participation_type: e.target.value }))}>
            {topic?.is_sponsored && <option value="lecturer_sponsored">With Alumni Sponsorship</option>}
            <option value="lecturer_unsponsored">Without Sponsorship</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Statement of Interest *</label>
          <textarea className="form-textarea" style={{ minHeight: 120 }}
            placeholder="Explain why you are interested in this topic and what makes you a good fit…"
            value={form.statement_of_interest} onChange={e => setForm(f => ({ ...f, statement_of_interest: e.target.value }))} required />
        </div>
        <div className="form-group">
          <label className="form-label">Relevant Experience</label>
          <textarea className="form-textarea" style={{ minHeight: 80 }}
            placeholder="Describe any relevant coursework, projects, or skills…"
            value={form.relevant_experience} onChange={e => setForm(f => ({ ...f, relevant_experience: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Proposed Approach</label>
          <textarea className="form-textarea" style={{ minHeight: 80 }}
            placeholder="Briefly outline how you would approach this research topic…"
            value={form.proposed_approach} onChange={e => setForm(f => ({ ...f, proposed_approach: e.target.value }))} />
        </div>
        <div className="form-group">
          <label className="form-label">Supporting Document (PDF)</label>
          <input type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files[0])} style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }} />
          {file && <span style={{ fontSize: '0.8rem', color: 'var(--teal)' }}>✓ {file.name}</span>}
        </div>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? <Spinner size={16} /> : 'Submit Application'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ── Sponsor Modal ─────────────────────────────────────────────────────────────
const AMOUNTS = [100000, 150000, 200000, 250000, 300000];

function SponsorModal({ topic, onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ amount: '', selection_criteria: '', notes: '', anonymous: false });
  const [receipt, setReceipt] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bankDetails, setBankDetails] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);

  const resetAndClose = () => { onClose(); setStep(1); setBankDetails(null); setForm({ amount: '', selection_criteria: '', notes: '', anonymous: false }); setReceipt(null); };

  const handleProceed = async () => {
    if (!form.amount) { toast.error('Please select a sponsorship amount'); return; }
    if (topic.department_id) {
      setBankLoading(true);
      try { const res = await departmentsAPI.getBankDetails(topic.department_id); setBankDetails(res.data); }
      catch { setBankDetails(null); }
      finally { setBankLoading(false); }
    }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!form.amount) { toast.error('Please select a sponsorship amount'); return; }
    setLoading(true);
    try {
      const res = await sponsorshipsAPI.create({ ...form, topic_id: topic.id, amount: parseFloat(form.amount) });
      if (receipt && res.data?.sponsorship?.id) {
        try {
          const fd = new FormData();
          fd.append('receipt', receipt);
          await api.post(`/sponsorships/${res.data.sponsorship.id}/receipt`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch { /* non-fatal */ }
      }
      toast.success('Sponsorship pledged successfully!');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create sponsorship');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={!!topic} onClose={resetAndClose} title={step === 1 ? `Sponsor: ${topic?.title || ''}` : 'Payment Details'}>
      {step === 1 ? (
        <form onSubmit={e => { e.preventDefault(); handleProceed(); }} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <Alert type="info">By pledging sponsorship, you commit to funding and mentoring the selected student project.</Alert>
          <div className="form-group">
            <label className="form-label">Sponsorship Amount *</label>
            <select className="form-select" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required>
              <option value="">Select amount…</option>
              {AMOUNTS.map(a => <option key={a} value={a}>{formatCurrency(a)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Selection Criteria</label>
            <textarea className="form-textarea" style={{ minHeight: 80 }}
              placeholder="What qualities are you looking for in an awardee?"
              value={form.selection_criteria} onChange={e => setForm(f => ({ ...f, selection_criteria: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes to Applicants</label>
            <textarea className="form-textarea" style={{ minHeight: 60 }}
              placeholder="Any message for prospective applicants…"
              value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={resetAndClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={bankLoading}>
              {bankLoading ? <Spinner size={16} /> : <>₦ Proceed to Payment</>}
            </button>
          </div>
        </form>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--gold)', fontWeight: 600, marginBottom: 4 }}>{formatCurrency(form.amount)}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Please wire this amount to the account below</div>
          </div>
          {bankDetails?.account_number ? (
            <div style={{ background: 'rgba(45,212,191,0.06)', border: '1px solid rgba(45,212,191,0.2)', borderRadius: 12, padding: 24 }}>
              <div style={{ fontSize: '0.7rem', color: 'var(--teal)', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 16 }}>Department Bank Account</div>
              {[['Department', bankDetails.name], ['Bank', bankDetails.bank_name], ['Account Number', bankDetails.account_number], ['Account Name', bankDetails.account_name]].map(([label, value]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{label}</span>
                  <span style={{ fontSize: '0.9rem', fontWeight: 600, fontFamily: label === 'Account Number' ? 'var(--font-mono)' : 'inherit' }}>{value}</span>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <p style={{ color: 'var(--warning)', fontSize: '0.9rem' }}>Bank account details not yet configured for this department.</p>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.8rem', marginTop: 8 }}>Please contact <a href="mailto:admin@futurumawards.com" style={{ color: 'var(--teal)' }}>admin@futurumawards.com</a> for payment details.</p>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Upload Bank Transfer Receipt</label>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => setReceipt(e.target.files[0])} style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }} />
            {receipt && <span style={{ fontSize: '0.8rem', color: 'var(--teal)' }}>✓ {receipt.name}</span>}
            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>The admin will be notified to confirm receipt of funds.</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <input type="checkbox" id="anon" checked={form.anonymous} onChange={e => setForm(f => ({ ...f, anonymous: e.target.checked }))} style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="anon" style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>List me as "Anonymous Donor" on the Hall of Honour</label>
          </div>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setStep(1)}>← Back</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || !bankDetails?.account_number}>
              {loading ? <Spinner size={16} /> : <>₦ Confirm Sponsorship</>}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}
